import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Card, Checkbox, message as antMessage, Spin, Tabs, Table, Badge, Tag, Tooltip } from 'antd'
import { DownOutlined, RightOutlined } from '@ant-design/icons'
import { useLanguage } from '../../../components/context/LanguageContext'
import { apiDownloadRequest, apiRequest } from '../../../helpers/apiRequest'
import JSONPretty from "react-json-pretty";
import { ReloadOutlined } from '@ant-design/icons'
import {
  FaFilePdf,
  FaFileImage,
  FaFileWord,
  FaFileExcel,
  FaFileAlt,
  FaFilePowerpoint,
  FaFileArchive,
  FaFileVideo,
  FaFileAudio,
} from 'react-icons/fa'

const IMAGE_EXTENSIONS = new Set(['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.tiff', '.tif', '.webp', '.svg', '.heic'])
const PDF_EXTENSIONS = new Set(['.pdf'])
const WORD_EXTENSIONS = new Set(['.doc', '.docx', '.rtf'])
const EXCEL_EXTENSIONS = new Set(['.xls', '.xlsx', '.csv'])
const POWERPOINT_EXTENSIONS = new Set(['.ppt', '.pptx'])
const ARCHIVE_EXTENSIONS = new Set(['.zip', '.rar', '.7z', '.tar', '.gz'])
const AUDIO_EXTENSIONS = new Set(['.mp3', '.wav', '.aac', '.flac', '.ogg'])
const VIDEO_EXTENSIONS = new Set(['.mp4', '.webm', '.mov', '.avi', '.mkv'])

const apiUrl = process.env.REACT_APP_API

const monedaColorGlobal = { RON: 'green', EUR: 'blue', USD: 'cyan' }
const normMoneda = (m) => { if (!m) return m; const u = m.trim().toUpperCase(); if (u === 'EURO') return 'EUR'; return u }

const subColsFirma = (monedaColor, eurRate) => ([
  { title: 'Firma', dataIndex: 'firma', key: 'firma', render: (v, r) => <div><div>{v || <span style={{ color: '#aaa' }}>—</span>}</div>{r.email && <small style={{ color: '#888' }}>{r.email}</small>}</div> },
  { title: 'Pret', key: 'pret', render: (_, r) => r.pret != null ? <><strong>{Number(r.pret).toLocaleString('ro-RO', { minimumFractionDigits: 2 })}</strong> <Tag color={monedaColor[normMoneda(r.moneda)] || 'default'}>{normMoneda(r.moneda)}</Tag></> : <span style={{ color: '#aaa' }}>—</span> },
  { title: 'Pret Euro', key: 'pretEur', render: (_, r) => {
    if (r.pret == null) return <span style={{ color: '#aaa' }}>—</span>
    const m = normMoneda(r.moneda)
    let pretEur = null
    if (m === 'EUR') pretEur = Number(r.pret)
    else if (m === 'RON' && eurRate) pretEur = Number(r.pret) / eurRate
    if (pretEur == null) return <span style={{ color: '#aaa' }}>—</span>
    return <><strong>{pretEur.toLocaleString('ro-RO', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</strong> <Tag color="blue">EUR</Tag></>
  }},
  { title: 'Cantitate', dataIndex: 'cantitate', key: 'cantitate', render: (v) => v ?? <span style={{ color: '#aaa' }}>—</span> },
  { title: 'Data', dataIndex: 'dataOferta', key: 'dataOferta', render: (v) => v ? new Date(v).toLocaleDateString('ro-RO') : <span style={{ color: '#aaa' }}>—</span> },
])

const OfferExpandedContent = ({ partNumber, showOferte, showRfq, eurRate }) => {
  const [oferte, setOferte] = React.useState([])
  const [rfqs, setRfqs] = React.useState([])
  const [loadingO, setLoadingO] = React.useState(false)
  const [loadingR, setLoadingR] = React.useState(false)
  const cols = subColsFirma(monedaColorGlobal, eurRate)

  React.useEffect(() => {
    if (!showOferte) return
    setLoadingO(true)
    apiRequest(`${apiUrl}tpm/offers?filter.partNumber=${encodeURIComponent(partNumber)}&limit=200&sortBy=dataOferta:DESC`, 'GET')
      .then(res => setOferte(res?.data?.data || []))
      .catch(() => setOferte([]))
      .finally(() => setLoadingO(false))
  }, [partNumber, showOferte])

  React.useEffect(() => {
    if (!showRfq) return
    setLoadingR(true)
    apiRequest(`${apiUrl}tpm/rfq-items/by-part?partNumber=${encodeURIComponent(partNumber)}`, 'GET')
      .then(res => setRfqs(res?.data || []))
      .catch(() => setRfqs([]))
      .finally(() => setLoadingR(false))
  }, [partNumber, showRfq])

  if (!showOferte && !showRfq) return <div style={{ color: '#888', padding: '8px 16px' }}>Selectează cel puțin un tip de date.</div>

  return (
    <div style={{ margin: '0 16px' }}>
      {showOferte && (
        <div style={{ marginBottom: showRfq ? 16 : 0 }}>
          <div style={{ fontWeight: 600, marginBottom: 4, color: '#1677ff' }}>Oferte ({loadingO ? '...' : oferte.length})</div>
          {loadingO ? <Spin size="small" /> : oferte.length === 0
            ? <div style={{ color: '#888', fontSize: 12 }}>Nu există oferte în baza de date.</div>
            : <Table dataSource={oferte} columns={cols} rowKey="id" size="small" pagination={oferte.length > 10 ? { pageSize: 10, size: 'small', showTotal: (t) => `${t} oferte` } : false} />
          }
        </div>
      )}
      {showRfq && (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 4, color: '#722ed1' }}>RFQ / WTB ({loadingR ? '...' : rfqs.length})</div>
          {loadingR ? <Spin size="small" /> : rfqs.length === 0
            ? <div style={{ color: '#888', fontSize: 12 }}>Nu există cereri WTB în baza de date.</div>
            : <Table dataSource={rfqs} columns={cols} rowKey="id" size="small" pagination={rfqs.length > 10 ? { pageSize: 10, size: 'small', showTotal: (t) => `${t} cereri` } : false} />
          }
        </div>
      )}
    </div>
  )
}

// ── Component pentru tab-ul Oferte ──────────────────────
const TpmOffersTab = ({ aiResponse }) => {
  const monedaColor = monedaColorGlobal
  const [page, setPage] = React.useState(1)
  const [pageSize, setPageSize] = React.useState(10)
  const [expandedRowKeys, setExpandedRowKeys] = React.useState([])
  const [showOferte, setShowOferte] = React.useState(true)
  const [showRfq, setShowRfq] = React.useState(true)
  const [analysisResults, setAnalysisResults] = React.useState({})
  const [analyzing, setAnalyzing] = React.useState(false)
  const [exporting, setExporting] = React.useState(false)
  const [eurRate, setEurRate] = React.useState(null)

  React.useEffect(() => {
    apiRequest(`${apiUrl}curs-valutar`, 'GET')
      .then(res => { const r = (res?.data || [])[0]; if (r?.eur) setEurRate(Number(r.eur)) })
      .catch(() => {})
  }, [])

  if (aiResponse?.type !== 'offer') return null

  const parts = aiResponse?.parts || []
  const firma = aiResponse?.firma || null
  const emailContact = aiResponse?.emailContact || null
  const dataOferta = aiResponse?.dataOferta || null

  const offers = parts.map((part, idx) => ({
    _key: `${part.partNumber || ''}-${part.ean || ''}-${idx}`,
    partNumber: part.partNumber,
    ean: part.ean || null,
    denumire: part.denumire || null,
    brand: part.brand || null,
    firma,
    email: emailContact,
    pret: part.pret ?? null,
    moneda: part.moneda || null,
    cantitate: part.cantitate ?? null,
    observatii: part.observatii || null,
    dataOferta,
  }))

  const columns = [
    { title: 'Part Number', dataIndex: 'partNumber', key: 'partNumber', render: (v) => <span>{v}</span> },
    { title: 'EAN', dataIndex: 'ean', key: 'ean', render: (v) => v ? <span>{v}</span> : <span style={{ color: '#aaa' }}>—</span> },
    { title: 'Denumire', dataIndex: 'denumire', key: 'denumire', render: (v) => v || <span style={{ color: '#aaa' }}>—</span> },
    { title: 'Brand', dataIndex: 'brand', key: 'brand', render: (v) => v || <span style={{ color: '#aaa' }}>—</span> },
    { title: 'Pret', dataIndex: 'pret', key: 'pret', render: (v, r) => v != null
      ? <><strong>{Number(v).toLocaleString('ro-RO', { minimumFractionDigits: 2 })}</strong>{r.moneda ? <> <Tag color={monedaColor[normMoneda(r.moneda)] || 'default'}>{normMoneda(r.moneda)}</Tag></> : null}</>
      : <span style={{ color: '#aaa' }}>—</span> },
    { title: 'Pret Euro', key: 'pretEur', render: (_, r) => {
      if (r.pret == null) return <span style={{ color: '#aaa' }}>—</span>
      const m = normMoneda(r.moneda)
      let pretEur = null
      if (m === 'EUR') pretEur = Number(r.pret)
      else if (m === 'RON' && eurRate) pretEur = Number(r.pret) / eurRate
      if (pretEur == null) return <span style={{ color: '#aaa' }}>—</span>
      return <><strong>{pretEur.toLocaleString('ro-RO', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</strong> <Tag color="blue">EUR</Tag></>
    }},
    {
      title: 'WTB', key: 'wtbCount', width: 70, align: 'center',
      render: (_, r) => {
        const a = analysisResults[r.partNumber]
        if (!a) return <span style={{ color: '#aaa' }}>—</span>
        return <span style={{ background: a.wtbCount > 0 ? '#d9f7be' : undefined, padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>{a.wtbCount}</span>
      },
    },
    {
      title: 'Comparatie', key: 'comparatie', width: 150,
      render: (_, r) => {
        const a = analysisResults[r.partNumber]
        if (!a || !a.comparatieLabel) return <span style={{ color: '#aaa' }}>—</span>
        return (
          <Tooltip title={a.comparatieTooltip}>
            <span style={{ background: a.comparatieColor, padding: '2px 8px', borderRadius: 4, fontWeight: 600, whiteSpace: 'nowrap' }}>
              {a.comparatieLabel}
            </span>
          </Tooltip>
        )
      },
    },
    { title: 'Cantitate', dataIndex: 'cantitate', key: 'cantitate', render: (v) => v ?? <span style={{ color: '#aaa' }}>—</span> },
    { title: 'Observatii', dataIndex: 'observatii', key: 'observatii', render: (v) => v || <span style={{ color: '#aaa' }}>—</span> },
    { title: 'Data oferta', dataIndex: 'dataOferta', key: 'dataOferta', render: (v) => v ? new Date(v).toLocaleDateString('ro-RO') : <span style={{ color: '#aaa' }}>—</span> },
  ]

  const handleExport = async () => {
    setExporting(true)
    try {
      const XLSXModule = await import('xlsx')
      const XLSX = XLSXModule.default || XLSXModule
      const rows = []

      for (const offer of offers) {
        const pn = offer.partNumber
        const offerBase = {
          'Part Number O': pn || '',
          'Denumire O': offer.denumire || '',
          'Pret O': offer.pret != null ? `${Number(offer.pret).toFixed(2)} ${offer.moneda || ''}` : '',
          'Partener O': offer.firma || '',
          'Data O': offer.dataOferta ? new Date(offer.dataOferta).toLocaleDateString('ro-RO') : '',
        }

        const [offerRes, rfqRes] = await Promise.all([
          apiRequest(`${apiUrl}tpm/offers?filter.partNumber=${encodeURIComponent(pn)}&limit=200&sortBy=dataOferta:DESC`, 'GET').catch(() => null),
          apiRequest(`${apiUrl}tpm/rfq-items/by-part?partNumber=${encodeURIComponent(pn)}`, 'GET').catch(() => null),
        ])

        const dbOffers = offerRes?.data?.data || []
        const dbRfqs = rfqRes?.data || []
        const expandRows = [
          ...dbOffers.map(o => ({ ...offerBase, 'Part Number': o.partNumber || '', 'Denumire': o.part?.denumire || o.denumire || '', 'Pret': o.pret != null ? `${Number(o.pret).toFixed(2)} ${o.moneda || ''}` : '', 'Partener': o.firma || '', 'Data': o.dataOferta ? new Date(o.dataOferta).toLocaleDateString('ro-RO') : '', 'Tip': 'Oferta' })),
          ...dbRfqs.map(r => ({ ...offerBase, 'Part Number': r.partNumber || '', 'Denumire': r.part?.denumire || r.denumire || '', 'Pret': r.pret != null ? `${Number(r.pret).toFixed(2)} ${r.moneda || ''}` : '', 'Partener': r.firma || '', 'Data': r.dataOferta ? new Date(r.dataOferta).toLocaleDateString('ro-RO') : '', 'Tip': 'WTB' })),
        ]

        if (expandRows.length === 0) {
          rows.push({ ...offerBase, 'Part Number': '', 'Denumire': '', 'Pret': '', 'Partener': '', 'Data': '', 'Tip': '' })
        } else {
          rows.push(...expandRows)
        }
      }

      const ws = XLSX.utils.json_to_sheet(rows, {
        header: ['Part Number O', 'Denumire O', 'Pret O', 'Partener O', 'Data O', 'Part Number', 'Denumire', 'Pret', 'Partener', 'Data', 'Tip'],
      })
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Oferte')
      const partener = (aiResponse?.firma || 'unknown').replace(/[^a-zA-Z0-9\-_]/g, '_')
      const date = new Date().toISOString().slice(0, 10).replace(/-/g, '')
      const fileName = `${partener}-tpm-oferte-${date}.xlsx`
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
      const blob = new Blob([wbout], { type: 'application/octet-stream' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      antMessage.success(`Export generat: ${rows.length} rânduri`)
    } catch (e) {
      console.error('Export error', e)
      antMessage.error(`Export eșuat: ${e?.message || e}`)
    } finally {
      setExporting(false)
    }
  }

  const handleAnalyze = async () => {
    setAnalyzing(true)
    const uniquePartNumbers = [...new Set(offers.map(o => o.partNumber).filter(Boolean))]
    const results = {}

    await Promise.all(uniquePartNumbers.map(async (pn) => {
      const offer = offers.find(o => o.partNumber === pn)
      const [offerRes, rfqRes] = await Promise.all([
        apiRequest(`${apiUrl}tpm/offers?filter.partNumber=${encodeURIComponent(pn)}&limit=200&sortBy=dataOferta:DESC`, 'GET').catch(() => null),
        apiRequest(`${apiUrl}tpm/rfq-items/by-part?partNumber=${encodeURIComponent(pn)}`, 'GET').catch(() => null),
      ])

      const dbOffers = offerRes?.data?.data || []
      const wtbItems = rfqRes?.data || []
      const wtbCount = wtbItems.length

      let comparatieLabel = null
      let comparatieColor = null
      let comparatieTooltip = null

      if (offer?.pret != null) {
        const offerMoneda = normMoneda(offer.moneda)
        const sameCurrency = dbOffers.filter(o => normMoneda(o.moneda) === offerMoneda && o.pret != null)
        if (sameCurrency.length > 0) {
          const prices = sameCurrency.map(o => Number(o.pret))
          const minP = Math.min(...prices)
          const maxP = Math.max(...prices)
          const avgP = prices.reduce((a, b) => a + b, 0) / prices.length
          const p = Number(offer.pret)

          if (p < minP) {
            const pct = ((minP - p) / minP * 100).toFixed(1)
            comparatieLabel = `-${pct}% față de minim`
            comparatieColor = '#d9f7be'
            comparatieTooltip = `Oferta (${p}) e mai mică decât minimul din DB (${minP.toFixed(2)} ${offerMoneda})`
          } else if (p <= maxP) {
            const diff = p - avgP
            const pct = Math.abs(diff / avgP * 100).toFixed(1)
            comparatieLabel = diff <= 0 ? `-${pct}% față de medie` : `+${pct}% față de medie`
            comparatieColor = '#fffbe6'
            comparatieTooltip = `Media prețurilor din DB: ${avgP.toFixed(2)} ${offerMoneda}`
          } else {
            const pct = ((p - maxP) / maxP * 100).toFixed(1)
            comparatieLabel = `+${pct}% față de maxim`
            comparatieColor = '#fff1f0'
            comparatieTooltip = `Oferta (${p}) e mai mare decât maximul din DB (${maxP.toFixed(2)} ${offerMoneda})`
          }
        }
      }

      results[pn] = { wtbCount, comparatieLabel, comparatieColor, comparatieTooltip }
    }))

    setAnalysisResults(results)
    setAnalyzing(false)
  }

  const currentPageKeys = offers.slice((page - 1) * pageSize, page * pageSize).map(r => r._key)
  const allCurrentExpanded = currentPageKeys.length > 0 && currentPageKeys.every(k => expandedRowKeys.includes(k))

  const toggleExpandAll = () => {
    if (allCurrentExpanded) setExpandedRowKeys(prev => prev.filter(k => !currentPageKeys.includes(k)))
    else setExpandedRowKeys(prev => [...new Set([...prev, ...currentPageKeys])])
  }

  const handlePageChange = (p, ps) => { setPage(p); setPageSize(ps); setExpandedRowKeys([]) }

  const expandedRowRender = (record) => (
    <OfferExpandedContent partNumber={record.partNumber} showOferte={showOferte} showRfq={showRfq} eurRate={eurRate} />
  )

  if (offers.length === 0) return null

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <Checkbox checked={showOferte} onChange={e => setShowOferte(e.target.checked)}>Oferte</Checkbox>
          <Checkbox checked={showRfq} onChange={e => setShowRfq(e.target.checked)}>RFQ</Checkbox>
          <Button size="small" type="primary" loading={analyzing} onClick={handleAnalyze}>Analiza</Button>
          <Button size="small" loading={exporting} onClick={handleExport}>Export Excel</Button>
        </div>
        <Tooltip title={allCurrentExpanded ? 'Restrânge toate' : 'Expandează toate rândurile din pagina curentă'}>
          <Button size="small" icon={allCurrentExpanded ? <RightOutlined /> : <DownOutlined />} onClick={toggleExpandAll}>
            {allCurrentExpanded ? 'Restrânge toate' : 'Expandează toate'}
          </Button>
        </Tooltip>
      </div>
      <Table
        dataSource={offers}
        columns={columns}
        rowKey="_key"
        size="small"
        pagination={{
          current: page, pageSize, total: offers.length,
          onChange: handlePageChange,
          showSizeChanger: true, pageSizeOptions: [10, 20, 50],
          showTotal: (t) => `${t} articole`,
        }}
        expandable={{
          expandedRowRender,
          expandedRowKeys,
          onExpand: (expanded, record) => {
            setExpandedRowKeys(prev => expanded ? [...prev, record._key] : prev.filter(k => k !== record._key))
          },
        }}
      />
    </div>
  )
}

// ── Component pentru tab-ul WTB ─────────────────────────
// Rows construite direct din JSON-ul AI. ExpandedOffers face in continuare fetch la BD dupa partNumber.
const TpmRfqTab = ({ aiResponse }) => {
  const monedaColor = { RON: 'green', EUR: 'blue', USD: 'cyan' }
  const [page, setPage] = React.useState(1)
  const [pageSize, setPageSize] = React.useState(10)
  const [expandedRowKeys, setExpandedRowKeys] = React.useState([])

  if (aiResponse?.type !== 'rfq') return null

  const parts = aiResponse?.parts || []
  const firma = aiResponse?.firma || null
  const emailContact = aiResponse?.emailContact || null
  const dataOferta = aiResponse?.dataOferta || null

  // Construim rows direct din parts-urile JSON-ului AI
  const rows = parts.map((part, idx) => ({
    key: `${part.partNumber || ''}-${part.ean || ''}-${idx}`,
    partNumber: part.partNumber,
    ean: part.ean || null,
    denumire: part.denumire,
    brand: part.brand || null,
    firma,
    email: emailContact,
    pret: part.pret ?? null,
    moneda: part.moneda || null,
    cantitate: part.cantitate ?? null,
    observatii: part.observatii || null,
    dataRfq: dataOferta,
  }))

  const currentPageKeys = rows.slice((page - 1) * pageSize, page * pageSize).map(r => r.key)
  const allCurrentExpanded = currentPageKeys.length > 0 && currentPageKeys.every(k => expandedRowKeys.includes(k))

  const toggleExpandAll = () => {
    if (allCurrentExpanded) {
      setExpandedRowKeys(prev => prev.filter(k => !currentPageKeys.includes(k)))
    } else {
      setExpandedRowKeys(prev => [...new Set([...prev, ...currentPageKeys])])
    }
  }

  const handlePageChange = (p, ps) => {
    setPage(p)
    setPageSize(ps)
    setExpandedRowKeys([])
  }

  const columns = [
    {
      title: 'Part Number',
      dataIndex: 'partNumber',
      key: 'partNumber',
      render: (v) => <span>{v}</span>,
    },
    {
      title: 'EAN',
      dataIndex: 'ean',
      key: 'ean',
      render: (v) => v ? <span>{v}</span> : <span style={{ color: '#aaa' }}>—</span>,
    },
    {
      title: 'Denumire',
      dataIndex: 'denumire',
      key: 'denumire',
      render: (v) => v || <span style={{ color: '#aaa' }}>—</span>,
    },
    {
      title: 'Brand',
      dataIndex: 'brand',
      key: 'brand',
      render: (v) => v || <span style={{ color: '#aaa' }}>—</span>,
    },
    {
      title: 'Firma',
      key: 'firma',
      render: (_, r) => (
        <div>
          <div>{r.firma || <span style={{ color: '#aaa' }}>—</span>}</div>
          {r.email && <small style={{ color: '#888' }}>{r.email}</small>}
        </div>
      ),
    },
    {
      title: 'Pret',
      key: 'pret',
      render: (_, r) => r.pret != null
        ? <><strong>{Number(r.pret).toLocaleString('ro-RO', { minimumFractionDigits: 2 })}</strong> <Tag color={monedaColor[normMoneda(r.moneda)] || 'default'}>{normMoneda(r.moneda)}</Tag></>
        : <span style={{ color: '#aaa' }}>—</span>,
    },
    {
      title: 'Cantitate',
      dataIndex: 'cantitate',
      key: 'cantitate',
      render: (v) => v ?? <span style={{ color: '#aaa' }}>—</span>,
    },
    {
      title: 'Observatii',
      dataIndex: 'observatii',
      key: 'observatii',
      render: (v) => v || <span style={{ color: '#aaa' }}>—</span>,
    },
    {
      title: 'Data RFQ',
      dataIndex: 'dataRfq',
      key: 'dataRfq',
      render: (v) => v ? new Date(v).toLocaleDateString('ro-RO') : <span style={{ color: '#aaa' }}>—</span>,
    },
  ]

  const offerColumns = [
    { title: 'Firma', dataIndex: 'firma', key: 'firma', render: (v, r) => (
      <div>
        <div>{v || <span style={{ color: '#aaa' }}>—</span>}</div>
        {r.email && <small style={{ color: '#888' }}>{r.email}</small>}
      </div>
    )},
    { title: 'Pret', key: 'pret', render: (_, r) => r.pret != null
      ? <><strong>{Number(r.pret).toLocaleString('ro-RO', { minimumFractionDigits: 2 })}</strong> <Tag color={monedaColor[normMoneda(r.moneda)] || 'default'}>{normMoneda(r.moneda)}</Tag></>
      : <span style={{ color: '#aaa' }}>—</span> },
    { title: 'Cantitate', dataIndex: 'cantitate', key: 'cantitate', render: (v) => v ?? <span style={{ color: '#aaa' }}>—</span> },
    { title: 'Data oferta', dataIndex: 'dataOferta', key: 'dataOferta', render: (v) => v ? new Date(v).toLocaleDateString('ro-RO') : <span style={{ color: '#aaa' }}>—</span> },
  ]

  const ExpandedOffers = ({ partNumber }) => {
    const [offers, setOffers] = React.useState([])
    const [loading, setLoading] = React.useState(true)
    const [subPage, setSubPage] = React.useState(1)
    const [subPageSize, setSubPageSize] = React.useState(10)

    React.useEffect(() => {
      apiRequest(`${apiUrl}tpm/offers?filter.partNumber=${encodeURIComponent(partNumber)}&limit=200&sortBy=dataOferta:DESC`, 'GET')
        .then(res => setOffers(res?.data?.data || []))
        .catch(() => setOffers([]))
        .finally(() => setLoading(false))
    }, [partNumber])

    if (loading) return <Spin size="small" style={{ margin: '8px 16px' }} />
    if (offers.length === 0) return <div style={{ color: '#888', padding: '8px 16px' }}>Nu exista oferte in baza de date pentru acest articol.</div>
    return (
      <Table
        dataSource={offers}
        columns={offerColumns}
        rowKey="id"
        size="small"
        pagination={offers.length > subPageSize ? {
          current: subPage, pageSize: subPageSize, total: offers.length,
          onChange: (p, ps) => { setSubPage(p); setSubPageSize(ps) },
          showSizeChanger: true, pageSizeOptions: [10, 20, 50],
          showTotal: (t) => `${t} oferte`,
          size: 'small',
        } : false}
        rowClassName={(_, idx) => idx === 0 ? 'ant-table-row-selected' : ''}
        style={{ margin: '0 16px' }}
      />
    )
  }

  const expandedRowRender = (record) => <ExpandedOffers partNumber={record.partNumber} />

  if (rows.length === 0) return null
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <Tooltip title={allCurrentExpanded ? 'Restrânge toate' : 'Expandează toate rândurile din pagina curentă'}>
          <Button size="small" icon={allCurrentExpanded ? <RightOutlined /> : <DownOutlined />} onClick={toggleExpandAll}>
            {allCurrentExpanded ? 'Restrânge toate' : 'Expandează toate'}
          </Button>
        </Tooltip>
      </div>
      <Table
        dataSource={rows}
        columns={columns}
        rowKey="key"
        size="small"
        pagination={{
          current: page, pageSize, total: rows.length,
          onChange: handlePageChange,
          showSizeChanger: true, pageSizeOptions: [10, 20, 50],
          showTotal: (t) => `${t} articole`,
        }}
        expandable={{
          expandedRowRender,
          expandedRowKeys,
          onExpand: (expanded, record) => {
            setExpandedRowKeys(prev =>
              expanded ? [...prev, record.key] : prev.filter(k => k !== record.key)
            )
          },
          rowExpandable: () => true,
        }}
      />
    </div>
  )
}

// ── Component principal ──────────────────────────────────────
const EmailMessageDetails = ({ message }) => {
  const languageContext = useLanguage()
  const [details, setDetails] = useState(null)
  const [loading, setLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [resendToERP, setResendToERP] = useState(false)

  useEffect(() => {
    let isCancelled = false
    const fetchDetails = async () => {
      if (!message?.id) { setDetails(null); setHasError(false); setLoading(false); return }
      setLoading(true)
      setHasError(false)
      const endpoint = `${process.env.REACT_APP_API}emails/${message.id}`
      const response = await apiRequest(endpoint, 'GET')
      if (isCancelled) return
      if (response?.ok) { setDetails(response.data ?? null); setHasError(false) }
      else { setDetails(null); setHasError(true) }
      setLoading(false)
    }
    fetchDetails()
    return () => { isCancelled = true }
  }, [message?.id, message?.processedAt, message?.status])

  const data = useMemo(() => details || message, [details, message])

  const translate = useCallback((key, fallback) => {
    const value = languageContext.t(key)
    if (!value || value.startsWith('[')) return fallback ?? key
    return value
  }, [languageContext])

  const attachments = useMemo(() => {
    const raw = data?.metadata?.attachments
    return Array.isArray(raw) ? raw : []
  }, [data])

  const rawHtmlContent = useMemo(() => {
    if (!data) return ''
    if (data.bodyHtml) return data.bodyHtml
    if (data.body) return data.body.replace(/\n/g, '<br />')
    return ''
  }, [data])

  const textContent = useMemo(() => {
    if (!data) return ''
    if (data.body) return data.body
    if (!rawHtmlContent) return ''
    try {
      const parser = new DOMParser()
      const doc = parser.parseFromString(rawHtmlContent, 'text/html')
      return doc.body.textContent || ''
    } catch (_error) {
      return rawHtmlContent.replace(/<[^>]+>/g, '')
    }
  }, [data, rawHtmlContent])

  const sanitizeExternalSources = useCallback(html => {
    if (!html) return ''
    try {
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')
      const isExternalUrl = url => {
        if (!url) return false
        const trimmed = url.trim().toLowerCase()
        return trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('//')
      }
      doc.querySelectorAll('[src]').forEach(element => { if (isExternalUrl(element.getAttribute('src'))) element.removeAttribute('src') })
      doc.querySelectorAll('a[href]').forEach(anchor => { if (isExternalUrl(anchor.getAttribute('href'))) anchor.removeAttribute('href') })
      return doc.body.innerHTML || ''
    } catch (_error) { return html }
  }, [])

  const sanitizedHtmlContent = useMemo(() => sanitizeExternalSources(rawHtmlContent), [rawHtmlContent, sanitizeExternalSources])

  const renderAttachmentMeta = attachment => {
    const parts = []
    if (attachment?.contentType) parts.push(attachment.contentType)
    if (typeof attachment?.size === 'number') parts.push(`${(attachment.size / 1024).toFixed(1)} KB`)
    if (attachment?.isInline) parts.push(translate('Inline', 'Inline'))
    return parts.join(' · ')
  }

  const getAttachmentExtension = attachment => {
    if (!attachment) return ''
    const fromField = attachment.fileExtension || attachment.fileName || attachment.name || ''
    if (typeof fromField === 'string' && fromField.includes('.')) {
      const extMatch = /(\.[^./\\]+)$/.exec(fromField)
      if (extMatch) return extMatch[1].toLowerCase()
    }
    return (attachment.fileExtension || '').toLowerCase()
  }

  const getAttachmentContentType = attachment => (attachment?.contentType || '').toLowerCase()

  const isImageAttachment = useCallback(attachment => {
    const type = getAttachmentContentType(attachment)
    if (type.startsWith('image/')) return true
    return IMAGE_EXTENSIONS.has(getAttachmentExtension(attachment))
  }, [])

  const isPreviewableAttachment = useCallback(attachment => {
    if (!attachment) return false
    if (isImageAttachment(attachment)) return true
    const type = getAttachmentContentType(attachment)
    if (type === 'application/pdf') return true
    const ext = getAttachmentExtension(attachment)
    if (PDF_EXTENSIONS.has(ext)) return true
    if (type.startsWith('text/')) return true
    if (['application/json'].includes(type)) return true
    return false
  }, [isImageAttachment])

  const getAttachmentIcon = attachment => {
    if (isImageAttachment(attachment)) return <FaFileImage />
    const type = getAttachmentContentType(attachment)
    const ext = getAttachmentExtension(attachment)
    if (type === 'application/pdf' || PDF_EXTENSIONS.has(ext)) return <FaFilePdf />
    if (WORD_EXTENSIONS.has(ext)) return <FaFileWord />
    if (EXCEL_EXTENSIONS.has(ext)) return <FaFileExcel />
    if (POWERPOINT_EXTENSIONS.has(ext)) return <FaFilePowerpoint />
    if (ARCHIVE_EXTENSIONS.has(ext)) return <FaFileArchive />
    if (AUDIO_EXTENSIONS.has(ext) || type.startsWith('audio/')) return <FaFileAudio />
    if (VIDEO_EXTENSIONS.has(ext) || type.startsWith('video/')) return <FaFileVideo />
    return <FaFileAlt />
  }

  const buildStoredAttachmentUrl = useCallback((attachment, endpoint) => {
    if (attachment?.filePath && attachment?.downloadType && attachment?.collectionIndex != null && data?.id) {
      return `${process.env.REACT_APP_API}emails/${data.id}/attachments/${attachment.downloadType}/${attachment.collectionIndex}/${endpoint}`
    }
    return null
  }, [data?.id])

  const buildDownloadUrl = useCallback(attachment => buildStoredAttachmentUrl(attachment, 'download') || attachment?.url || null, [buildStoredAttachmentUrl])
  const buildViewUrl = useCallback(attachment => buildStoredAttachmentUrl(attachment, 'view') || attachment?.url || null, [buildStoredAttachmentUrl])

  const openAttachment = useCallback((attachment, event) => {
    if (event) event.preventDefault()
    if (!attachment) return
    const previewable = isPreviewableAttachment(attachment)
    const viewUrl = buildViewUrl(attachment)
    if (previewable && viewUrl) { window.open(viewUrl, '_blank', 'noopener,noreferrer'); return }
    const downloadUrl = buildDownloadUrl(attachment)
    if (!downloadUrl) return
    const fileName = attachment.fileName || attachment.name || `${attachment.downloadType ?? 'file'}-${attachment.collectionIndex ?? '0'}`
    if (attachment?.filePath && attachment?.downloadType && attachment?.collectionIndex != null && data?.id) {
      apiDownloadRequest(downloadUrl, fileName, attachment.contentType)
    } else {
      window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    }
  }, [buildDownloadUrl, buildViewUrl, data?.id, isPreviewableAttachment])

  const handleReprocess = useCallback(async () => {
    if (!message?.id || processing) return
    setProcessing(true)
    const endpoint = `${process.env.REACT_APP_API}emails/${message.id}/process`
    try {
      const response = await apiRequest(endpoint, 'POST', {})
      if (response?.ok) antMessage.success(translate('Email reprocessed successfully', 'Email reprocessed successfully'))
      else antMessage.error(translate('Failed to reprocess email', 'Failed to reprocess email'))
    } catch (error) {
      antMessage.error(translate('Error reprocessing email', 'Error reprocessing email'))
    } finally {
      setProcessing(false)
    }
    const detailsResponse = await apiRequest(`${process.env.REACT_APP_API}emails/${message.id}`, 'GET')
    if (detailsResponse?.ok) setDetails(detailsResponse.data ?? null)
  }, [message?.id, processing, translate])

  const handleResendERP = useCallback(async () => {
    if (!message?.id || resendToERP) return
    setResendToERP(true)
    try {
      const response = await apiRequest(`${process.env.REACT_APP_API}emails/${message.id}/sendtoerp`, 'POST', {})
      if (response?.ok) antMessage.success(translate('Email resent to ERP successfully', 'Email resent to ERP successfully'))
      else antMessage.error(translate('Failed to resend email to ERP', 'Failed to resend email to ERP'))
    } catch (error) {
      antMessage.error(translate('Error resending email to ERP', 'Error resending email to ERP'))
    } finally {
      setResendToERP(false)
    }
    const erpResponse = await apiRequest(`${process.env.REACT_APP_API}emails/${message.id}`, 'GET')
    if (erpResponse?.ok) setDetails(erpResponse.data ?? null)
  }, [message?.id, resendToERP, translate])

  // Parse aiResponse o singura data din data.response - sursa de adevar pentru TPM tabs
  const aiResponse = useMemo(() => {
    try { return JSON.parse(data?.response || '{}') } catch { return {} }
  }, [data?.response])

  // Badge-uri calculate direct din JSON, fara fetch la BD
  const offerCount = aiResponse?.type === 'offer' ? (aiResponse?.parts?.length ?? null) : null
  const rfqCount = aiResponse?.type === 'rfq' ? (aiResponse?.parts?.length ?? null) : null

  const attachmentStyles = useMemo(() => ({
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16, margin: 0, padding: 10 },
    card: { listStyle: 'none', background: '#ffffff', border: '1px solid #e9ecef', borderRadius: 12, overflow: 'hidden', boxShadow: '0 8px 16px -12px rgba(15, 23, 42, 0.45)', display: 'flex', flexDirection: 'column', transition: 'transform 0.2s ease, box-shadow 0.2s ease' },
    preview: { height: 200, background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', overflow: 'hidden' },
    previewImage: { width: '100%', height: '100%', objectFit: 'cover' },
    icon: { fontSize: 56, color: '#6c757d' },
    details: { padding: '14px 16px 18px', display: 'flex', flexDirection: 'column', gap: 6 },
    nameLink: { fontWeight: 600, fontSize: 15, color: '#0d6efd', textDecoration: 'none', wordBreak: 'break-word' },
    meta: { color: '#6c757d', fontSize: 12, lineHeight: 1.4 },
    stored: { color: '#198754', fontSize: 12 },
    error: { color: '#dc3545', fontSize: 12 },
  }), [])

  if (!message?.id) return null

  const tpmTabLabel = offerCount != null && offerCount > 0
    ? <span>Oferte <Badge count={offerCount} overflowCount={999} style={{ backgroundColor: '#52c41a' }} /></span>
    : 'Oferte'

  return (
    <Card className="mt-3">
      {hasError && (
        <Alert type="error" message={translate('Error', 'Error')}
          description={translate('Could not load email message details', 'Could not load email message details')}
          style={{ marginBottom: 16 }} />
      )}
      <Spin spinning={loading}>
        <Tabs
          defaultActiveKey="details"
          type="card"
        >
          <Tabs.TabPane tab={translate('Details', 'Details')} key="details">
            <div style={{ marginBottom: 12 }}><strong>{translate('Subject', 'Subject')}:</strong> {data?.subject || '-'}</div>
            <div style={{ marginBottom: 12 }}><strong>{translate('From', 'From')}:</strong> {data?.from || '-'}</div>
            {data?.receivedAt && (
              <div style={{ marginBottom: 12 }}><strong>{translate('Received At', 'Received At')}:</strong> {new Date(data.receivedAt).toLocaleString()}</div>
            )}
            <div>
              <strong>{translate('Content', 'Content')}:</strong>
              <Tabs size="small" defaultActiveKey="html" type="card" style={{ marginTop: 8 }}>
                <Tabs.TabPane tab={translate('HTML', 'HTML')} key="html">
                  <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    dangerouslySetInnerHTML={{ __html: sanitizedHtmlContent?.replace(/\n/g, '<br />') || '-' }} />
                </Tabs.TabPane>
                <Tabs.TabPane tab={translate('Text', 'Text')} key="text">
                  <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{textContent || '-'}</div>
                </Tabs.TabPane>
              </Tabs>
            </div>
          </Tabs.TabPane>

          <Tabs.TabPane tab={translate('Attachments', 'Attachments')} key="attachments">
            {attachments.length > 0 ? (
              <div style={attachmentStyles.grid}>
                {attachments.map(attachment => {
                  const key = attachment.id || `${attachment.name ?? attachment.fileName ?? 'attachment'}-${attachment.url ?? ''}`
                  const meta = renderAttachmentMeta(attachment)
                  const isStored = attachment?.filePath && attachment?.downloadType && attachment?.collectionIndex != null && data?.id
                  const previewable = isPreviewableAttachment(attachment)
                  const downloadUrl = buildDownloadUrl(attachment)
                  const viewUrl = buildViewUrl(attachment)
                  const anchorHref = (previewable && viewUrl) || downloadUrl || '#'
                  const displayName = attachment.name || attachment.fileName || translate('Attachment', 'Attachment')
                  const icon = getAttachmentIcon(attachment)
                  const previewCursor = previewable || downloadUrl ? 'pointer' : 'not-allowed'
                  return (
                    <div key={key} style={attachmentStyles.card}>
                      <div style={{ ...attachmentStyles.preview, cursor: previewCursor }}
                        role="button" tabIndex={previewable || downloadUrl ? 0 : -1}
                        onClick={event => openAttachment(attachment, event)}
                        onKeyDown={event => { if (['Enter', ' ', 'Spacebar'].includes(event.key)) openAttachment(attachment, event) }}>
                        {isImageAttachment(attachment) && viewUrl
                          ? <img src={viewUrl} alt={displayName} style={attachmentStyles.previewImage} />
                          : <div style={attachmentStyles.icon}>{icon}</div>}
                      </div>
                      <div style={attachmentStyles.details}>
                        <a href={anchorHref} target="_blank" rel="noopener noreferrer" style={attachmentStyles.nameLink} onClick={event => openAttachment(attachment, event)}>
                          {displayName}
                        </a>
                        {meta && <div style={attachmentStyles.meta}>{meta}</div>}
                        {isStored && attachment.fileName && <div style={attachmentStyles.stored}>{translate('Downloaded File', 'Downloaded file')}: {attachment.fileName}</div>}
                        {attachment.downloadError && <div style={attachmentStyles.error}>{translate('Download Error', 'Download error')}: {attachment.downloadError}</div>}
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div style={{ color: '#6c757d' }}>{translate('No Attachments', 'No attachments available')}</div>
            )}
          </Tabs.TabPane>

          {/* Tab Oferte */}
          <Tabs.TabPane tab={tpmTabLabel} key="tpm-offers">
            <TpmOffersTab aiResponse={aiResponse} />
          </Tabs.TabPane>

          {/* Tab WTB */}
          <Tabs.TabPane
            tab={rfqCount != null && rfqCount > 0
              ? <span>WTB <Badge count={rfqCount} overflowCount={999} style={{ backgroundColor: '#1677ff' }} /></span>
              : 'WTB'}
            key="tpm-rfq"
          >
            <TpmRfqTab aiResponse={aiResponse} />
          </Tabs.TabPane>

          {data?.status !== 'pending' && data?.response && (
            <Tabs.TabPane tab={translate('Processing Results', 'Processing Results')} key="processing">
              <div style={{ marginBottom: 24 }}>
                <div style={{ marginBottom: 16 }}>
                  <strong>{translate('AI Response', 'AI Response')}:</strong>
                  <div style={{ marginTop: 8, padding: 12, background: '#f5f5f5', borderRadius: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    <JSONPretty data={data.response} />
                  </div>
                </div>
                {data.processingLog && (
                  <div>
                    <strong>{translate('Processing Log', 'Processing Log')}:</strong>
                    <div style={{ marginTop: 8, padding: 12, background: '#fafafa', borderRadius: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', fontSize: 12, maxHeight: 400, overflowY: 'auto' }}>
                      {data.processingLog}
                    </div>
                  </div>
                )}
              </div>
            </Tabs.TabPane>
          )}

          {data?.status !== 'pending' && data?.status !== 'error' && data?.erpResponse && (
            <Tabs.TabPane tab={translate('ERP Response', 'ERP Response')} key="erpResponse">
              <div style={{ marginTop: 8, padding: 12, background: '#f5f5f5', borderRadius: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                <JSONPretty data={data.erpResponse} />
              </div>
            </Tabs.TabPane>
          )}
        </Tabs>
      </Spin>
    </Card>
  )
}

export default EmailMessageDetails